import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '../../../environments/environment';

const apiPrefix = '/user-mgmt/v1/';

const routes = {
    getFilteredUsers: (page: number, size: number) => apiPrefix + `users?page=${page}&size=${size}`,
    getRoles: () => apiPrefix + `roles-as-list`,
    getUserByUserId: (userId: any) => apiPrefix + `users/${userId}`,
    getBusinessEntities: () => apiPrefix + `business-entities`,
    businessEntityGroups: () => apiPrefix + `business-entity-group`,
    getDomains: () => apiPrefix + `domain-vos`,
    domainGroup: () => apiPrefix + `domain-group`,
    user: () => apiPrefix + 'users',
    getCurrentUser: () => apiPrefix + `get-current-user`,
    userContacts: () => apiPrefix + `user-contact`,
    getDomainsByLoggedInUser: () => apiPrefix + `domains`,
    getEntitiesByUser: () => apiPrefix + `entities-by-user`,
    getEntitiesForCustomerOnBoard: () => apiPrefix + `business-entities-vo`
};

@Injectable({
    providedIn: 'root'
})
export class UserMgmtService {

    constructor(private http: HttpClient) {
    }

    getFilteredUsers(identifier: string, page: number, size: number) {

        const $filteredUsers: Observable<any> = this.http
            .get(routes.getFilteredUsers(page, size), {
                params: {
                    identifier
                }
            })
            .pipe(
                map(
                    (users) => {
                        return users as any;
                    }
                )
            );

        return $filteredUsers;
    }

    signUpNewBasicUser(signUpRequest: any) {
        const $user: Observable<any> = this.http
            .post(environment.authUrl + '/signup', signUpRequest, {
                responseType: 'text'
            })
            .pipe(
                map((user) => {
                    return user as any;
                })
            );

        return $user;
    }

    getUserByUserId(userId: any) {
        const $user: Observable<any> = this.http
            .get(routes.getUserByUserId(userId))
            .pipe(
                map((user) => {
                    return user as any;
                })
            );
        return $user;
    }

    updateUserDetails(userDetails: any) {
        const $user: Observable<any> = this.http
            .put(routes.user(), userDetails)
            .pipe(
                map((user) => {
                    return user as any;
                })
            );
        return $user;
    }

    getRoles() {
        const $roles: Observable<any> = this.http
            .get(routes.getRoles())
            .pipe(
                map((roles) => {
                    return roles as any;
                })
            );

        return $roles;
    }


    getBusinessEntities() {
        const $businessEntities: Observable<any> = this.http
            .get(routes.getBusinessEntities())
            .pipe(
                map((businessEntities) => {
                    return businessEntities as any;
                })
            );
        return $businessEntities;
    }

    getBusinessEntitiesByUser() {
        const $businessEntities: Observable<any> = this.http
            .get(routes.getBusinessEntities() + `-by-user`)
            .pipe(
                map((businessEntities) => {
                    return businessEntities as any;
                })
            );
        return $businessEntities;
    }

    getEntitiesByUser() {
        const $entities: Observable<any> = this.http
            .get(routes.getEntitiesByUser())
            .pipe(
                map((entities) => {
                    return entities as any;
                })
            );
        return $entities;
    }

    getBusinessEntityGroups() {
        const $businessEntityGroups: Observable<any> = this.http
            .get(routes.businessEntityGroups())
            .pipe(
                map((businessEntityGroups) => {
                    return businessEntityGroups as any;
                })
            );
        return $businessEntityGroups;
    }

    createBusinessEntityGroup(businessEntityGroup: any) {
        const $businessEntityGroup: Observable<any> = this.http
            .post(routes.businessEntityGroups(), businessEntityGroup)
            .pipe(
                map((businessEntityGroupResp) => {
                    return businessEntityGroupResp as any;
                })
            );
        return $businessEntityGroup;
    }

    updateBusinessEntityGroup(businessEntityGroup: any) {
        const $businessEntityGroup: Observable<any> = this.http
            .put(routes.businessEntityGroups(), businessEntityGroup)
            .pipe(
                map((businessEntityGroupResp) => {
                    return businessEntityGroupResp as any;
                })
            );
        return $businessEntityGroup;
    }


    getDomains() {
        const $domains: Observable<any> = this.http
            .get(routes.getDomains())
            .pipe(
                map((domains) => {
                    return domains as any;
                })
            );
        return $domains;
    }

    getDomainsByUserLoggedIn() {
        const $domains: Observable<any> = this.http
            .get(routes.getDomainsByLoggedInUser())
            .pipe(
                map((domains) => {
                    return domains as any;
                })
            );

        return $domains;
    }

    getDomainGroups() {
        const $domainGroups: Observable<any> = this.http
            .get(routes.domainGroup())
            .pipe(
                map((domainGroups) => {
                    return domainGroups as any;
                })
            );
        return $domainGroups;
    }

    createDomainGroup(domainGroup: any) {
        const $domainGroup: Observable<any> = this.http
            .post(routes.domainGroup(), domainGroup)
            .pipe(
                map((domainGroupResp) => {
                    return domainGroupResp as any;
                })
            );
        return $domainGroup;
    }

    updateDomainGroup(domainGroup: any) {
        const $domainGroup: Observable<any> = this.http
            .put(routes.domainGroup(), domainGroup)
            .pipe(
                map((domainGroupResp) => {
                    return domainGroupResp as any;
                })
            );
        return $domainGroup;
    }

    getCountries() {
        const $countries: Observable<any> = this.http
            .get(apiPrefix + '/countries')
            .pipe(
                map((countries) => {
                    return countries as any;
                })
            );
        return $countries;
    }

    getCurrentUser() {
        const $user: Observable<any> = this.http
            .get(routes.getCurrentUser())
            .pipe(
                map((user) => {
                    return user as any;
                })
            );
        return $user;
    }

    handleUserAccountStatus(user: any) {
        const userId = user?.userId;
        const enabled = user?.enabled;
        const $userAccountStatus: Observable<any> = this.http
            .put(environment.authUrl + `/users/${userId}/account-status`, null, {
                params: {
                    enabled
                }
            })
            .pipe(
                map((accountStatus) => {
                    return accountStatus as any;
                })
            );
        return $userAccountStatus;
    }

    handleUpdatePassword(signUpRequest: any) {
        const $userPassword: Observable<any> = this.http
            .put(environment.authUrl + `/users/pwd`, signUpRequest)
            .pipe(
                map((userPassword) => {
                    return userPassword as any;
                })
            );
        return $userPassword;
    }

    updateUserBasicDetails(userId: any, updateUserRequest: any) {
        const $user: Observable<any> = this.http
            .put(environment.authUrl + `/users/${userId}`, updateUserRequest)
            .pipe(map((user) => {
                return user as any;
            }));
        return $user;
    }

    getUserContacts(role: string) {
        const $userContacts: Observable<any> = this.http
            .get(routes.userContacts(),
                {
                    params: {
                        role
                    }
                })
            .pipe(
                map(
                    (x) => {
                        return x as any;
                    }
                )
            );
        return $userContacts;
    }

    createUserContact(role: string, userContact: any) {
        const $userContact: Observable<any> = this.http
            .post(routes.userContacts(), userContact, {
                params: {
                    role
                }
            }).pipe(
                map(
                    (userContactRes) => {
                        return userContactRes as any;
                    }
                )
            );
        return $userContact;
    }

    updateUserContact(userContact: any) {
        const $userContact: Observable<any> = this.http
            .put(routes.userContacts(), userContact).pipe(
                map(
                    (userContactRes) => {
                        return userContactRes as any;
                    }
                )
            );
        return $userContact;
    }

    getUserContactByUserContactId(userContactId: any) {
        const $userContact: Observable<any> = this.http
            .get(routes.userContacts() + `/${userContactId}`)
            .pipe(
                map((userContact) => {
                    return userContact as any;
                })
            );
        return $userContact;
    }

    getBusinessEntitiesForCustomerOnBoard() {
        const $entities: Observable<any> = this.http
            .get(routes.getEntitiesForCustomerOnBoard())
            .pipe(
                map((res) => {
                    return res as any;
                })
            );
        return $entities;
    }
}
